import { isRenderableNode } from '@prophecy/utils/react/nodes';
import { useEffect, useState, forwardRef } from 'react';

import { useTriggerContext } from '../Dropdown';
import { renderWithTooltip } from '../Tooltip';
import {
  StyledButton,
  StyledButtonLink,
  StyledButtonAnchor,
  StyledLoadingIcon,
  StyledIconWrapper,
  StyledButtonContent,
  ButtonWrapperForTooltip
} from './styled';
import {
  ButtonProps,
  ButtonAsType,
  ButtonRefType,
  ButtonVariants,
  ButtonSize,
  ButtonAs,
  IconPlacement,
  ButtonShape,
  ButtonCategory,
  StyleProps
} from './types';

function useLoading(loading?: ButtonProps['loading']) {
  const [isLoading, setIsLoading] = useState(!!loading);

  useEffect(() => {
    if (typeof loading === 'boolean') {
      setIsLoading(loading);
    } else if (typeof loading === 'object') {
      const timeout = setTimeout(() => {
        setIsLoading(true);
      }, loading.delay);

      return () => clearTimeout(timeout);
    } else {
      // Handle the case when loading is set to undefined
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(loading)]);

  return isLoading;
}

const ButtonComponentMap = {
  anchor: StyledButtonAnchor,
  button: StyledButton,
  link: StyledButtonLink
};

function _Button<T extends ButtonAsType = 'button'>(
  {
    variant = ButtonVariants.primary,
    icon,
    iconPlacement = IconPlacement.right,
    size = ButtonSize.m,
    shape = ButtonShape.default,
    loading,
    disabled,
    disableWhileLoading = true,
    elementType,
    children,
    block,
    danger,
    tooltip,
    onClick,
    disablePointerFocus,
    ...restProps
  }: ButtonProps<T>,
  forwardRef: React.Ref<ButtonRefType<T>>
) {
  const _as = (elementType ?? ButtonAs.button) as ButtonAs;
  const isLoading = useLoading(loading);
  const _disabled = disabled || (isLoading && disableWhileLoading);
  const hasIcon = isRenderableNode(icon);
  const hasChildren = isRenderableNode(children);
  const hasOnlyIcon = hasIcon && !hasChildren;

  const Component = ButtonComponentMap[_as] as React.ForwardRefRenderFunction<T, StyleProps>;

  const insideTrigger = useTriggerContext();

  /**
   * if a button is inside a dropdown trigger, map the onClick to the trigger's onPointerDown
   * As trigger prevent defaults on pointerDown, blocking click event for child components
   */
  const clickProp = _disabled ? undefined : insideTrigger ? { onPointerDown: onClick } : { onClick };

  let _variant = variant;
  // for danger button, there is no tertiary grey and link grey, so change that.

  if (danger && variant === ButtonVariants.tertiaryGray) {
    _variant = ButtonVariants.tertiary;
  } else if (danger && variant === ButtonVariants.linkGray) {
    _variant = ButtonVariants.link;
  }

  let button = (
    <Component
      type='button'
      ref={forwardRef}
      variant={_variant}
      size={size}
      disabled={_disabled}
      shape={shape}
      hasChildren={hasChildren}
      data-loading={isLoading}
      loading={isLoading}
      category={danger ? ButtonCategory.danger : ButtonCategory.default}
      block={block}
      disablePointerFocus={
        _variant === ButtonVariants.link ||
        _variant === ButtonVariants.linkGray ||
        _disabled ||
        block ||
        disablePointerFocus
      }
      {...clickProp}
      {...restProps}>
      {isLoading && <StyledLoadingIcon type='extended' $iconPlacement={iconPlacement} spin />}
      {hasChildren && <StyledButtonContent>{children}</StyledButtonContent>}
      {hasOnlyIcon && isLoading
        ? null
        : hasIcon && <StyledIconWrapper $iconPlacement={iconPlacement}>{icon}</StyledIconWrapper>}
    </Component>
  );

  if (tooltip) {
    /**
     * if elementType is link, it renders a Link Component, which doesn't have forward ref.
     * So if tooltip prop is added it doesn't have effect. To solve such case we can wrap it
     * inside span.
     */
    const _button =
      elementType === ButtonAs.link ? <ButtonWrapperForTooltip>{button}</ButtonWrapperForTooltip> : button;
    button = <>{renderWithTooltip(_button, tooltip)}</>;
  }

  return button;
}

// NOTE: Sadly forwardRef doesn't allow generic types, so we have to typecast it
export const Button = forwardRef(_Button) as unknown as <T extends ButtonAsType = 'button'>(
  p: ButtonProps<T> & { ref?: React.Ref<ButtonRefType<T>> }
) => React.ReactElement;
